import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { FiX } from 'react-icons/fi';
import LoadingSpinner from '../common/LoadingSpinner';
import { Modal } from '../../design-system';

const CommonSpaceForm = ({ commonSpace, onSave, onClose, existingSpaces = [] }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditing = !!commonSpace;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: commonSpace?.name || '',
      cleaning_time_minutes: commonSpace?.cleaning_time_minutes || 30,
      notes: commonSpace?.notes || '',
    },
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);

    try {
      const spaceData = {
        name: data.name,
        cleaning_time_minutes: parseInt(data.cleaning_time_minutes),
        notes: data.notes || null,
        is_active: true
      };

      await onSave(spaceData);
    } catch (error) {
      console.error('Error saving common space:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      size="lg"
      showCloseButton={false}
    >
      <Modal.Header>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">
            {isEditing ? 'Editar Espacio Común' : 'Nuevo Espacio Común'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <FiX className="h-6 w-6" />
          </button>
        </div>
      </Modal.Header>

      <Modal.Body>
        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" id="common-space-form">
              {/* Space name */}
              <div>
                <label htmlFor="name" className="form-label">
                  Nombre del espacio *
                </label>
                <input
                  id="name"
                  type="text"
                  className={`form-input ${errors.name ? 'border-danger-500' : ''}`}
                  placeholder="Zona común, Baño compartido, etc."
                  {...register('name', {
                    required: 'El nombre es requerido',
                    minLength: { value: 2, message: 'Debe tener al menos 2 caracteres' },
                    validate: (value) => {
                      // Check if name already exists (excluding current space when editing)
                      const trimmedValue = value.trim().toLowerCase();
                      const isDuplicate = existingSpaces.some(
                        s => s.name.toLowerCase() === trimmedValue && s.id !== commonSpace?.id
                      );
                      return !isDuplicate || 'Ya existe un espacio con este nombre';
                    }
                  })}
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-danger-600">
                    {errors.name.message}
                  </p>
                )}
              </div>

              {/* Cleaning time in minutes */}
              <div>
                <label htmlFor="cleaning_time_minutes" className="form-label">
                  Tiempo de limpieza (minutos) *
                </label>
                <input
                  id="cleaning_time_minutes"
                  type="number"
                  min="5"
                  max="180"
                  className={`form-input ${errors.cleaning_time_minutes ? 'border-danger-500' : ''}`}
                  placeholder="30"
                  {...register('cleaning_time_minutes', {
                    required: 'El tiempo de limpieza es requerido',
                    min: { value: 5, message: 'Debe ser al menos 5 minutos' },
                    max: { value: 180, message: 'Máximo 180 minutos (3 horas)' }
                  })}
                />
                {errors.cleaning_time_minutes && (
                  <p className="mt-1 text-sm text-danger-600">
                    {errors.cleaning_time_minutes.message}
                  </p>
                )}
                <p className="mt-1 text-sm text-gray-500">
                  Tiempo estimado que se tarda en limpiar este espacio
                </p>
              </div>

              {/* Notes */}
              <div>
                <label htmlFor="notes" className="form-label">
                  Notas
                </label>
                <textarea
                  id="notes"
                  rows={3}
                  className="form-input"
                  placeholder="Notas adicionales sobre el espacio..."
                  {...register('notes')}
                />
              </div>

        </form>
      </Modal.Body>

      <Modal.Footer>
        <button
          type="button"
          onClick={onClose}
          className="btn btn-secondary"
          disabled={isSubmitting}
        >
          Cancelar
        </button>
        <button
          type="submit"
          form="common-space-form"
          className="btn btn-primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <div className="flex items-center space-x-2">
              <LoadingSpinner size="sm" color="white" />
              <span>Guardando...</span>
            </div>
          ) : (
            isEditing ? 'Actualizar' : 'Crear'
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default CommonSpaceForm;
